import React, { useEffect, useState } from 'react';

interface SpotlightProps {
  targetId: string | null;
  onClose: () => void;
  title?: string;
  content?: string;
  footer?: string; // Large text (e.g., Seat Coords)
}

export const Spotlight: React.FC<SpotlightProps> = ({ targetId, onClose, title, content, footer }) => {
  const [position, setPosition] = useState<{ top: number, left: number, width: number, height: number } | null>(null);

  useEffect(() => {
    if (!targetId) return;

    let intervalId: ReturnType<typeof setInterval>;
    let timeoutId: ReturnType<typeof setTimeout>;
    let retryId: ReturnType<typeof setInterval>;
    
    // Function reference for cleanup
    let updatePosRef: (() => void) | null = null;

    const init = () => {
        const el = document.getElementById(targetId);
        if (!el) return false;

        // Found! Scroll into view
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

        const updatePos = () => {
            const rect = el.getBoundingClientRect();
            // Only update if the element has valid dimensions
            if (rect.width > 0 && rect.height > 0) {
                setPosition({
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height
                });
            }
        };
        
        updatePosRef = updatePos;

        // Update immediately
        updatePos();

        // Loop update for 1 second to track the smooth scroll animation
        intervalId = setInterval(updatePos, 16);
        timeoutId = setTimeout(() => clearInterval(intervalId), 1500);

        // Listen to resize and scroll
        window.addEventListener('resize', updatePos);
        window.addEventListener('scroll', updatePos, true);
        
        return true;
    };

    // Retry finding element if not immediately available
    if (!init()) {
        let attempts = 0;
        retryId = setInterval(() => {
            attempts++;
            if (init() || attempts > 50) { // Try for ~2.5s
                clearInterval(retryId);
            }
        }, 50);
    }

    return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        clearInterval(retryId);
        if (updatePosRef) {
            window.removeEventListener('resize', updatePosRef);
            window.removeEventListener('scroll', updatePosRef, true);
        }
    };
  }, [targetId]);

  if (!targetId || !position) return null;

  // Reduced padding for a tighter fit
  const PADDING = 4;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" onClick={onClose}>
      {/* Dark Overlay with "cutout" simulated by box-shadow */}
      <div 
        className="absolute transition-all duration-75 ease-out"
        style={{
          top: position.top - PADDING,
          left: position.left - PADDING,
          width: position.width + (PADDING * 2),
          height: position.height + (PADDING * 2),
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)', 
          borderRadius: '6px',
          pointerEvents: 'none'
        }}
      >
          {/* Pulsing Yellow Border to match the SeatMap internal highlight */}
          <div className="absolute inset-0 border-2 border-yellow-400 rounded-md animate-pulse shadow-[0_0_15px_rgba(250,204,21,0.6)]"></div>
      </div>

      {/* Popover / Tooltip */}
      <div 
        className="absolute bg-white p-4 rounded-xl shadow-2xl animate-bounce-small border border-gray-100 flex flex-col items-center justify-center gap-1"
        style={{
            // Position above or below depending on space
            top: position.top - 120 > 0 ? position.top - 130 : position.top + position.height + 20,
            left: Math.max(20, position.left + (position.width / 2) - 100),
            width: '240px',
            zIndex: 51
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && <div className="text-xs text-gray-500 font-bold uppercase tracking-wider">{title}</div>}
        {content && <div className="text-lg text-gray-800 font-bold leading-none my-1">{content}</div>}
        {footer && <div className="text-3xl text-primary font-extrabold mt-2 border-t border-gray-100 pt-2 w-full text-center">{footer}</div>}
        
        {/* Arrow */}
        <div 
            className="absolute left-1/2 -ml-2 w-4 h-4 bg-white transform rotate-45 border-r border-b border-gray-100"
            style={{ 
                bottom: position.top - 120 > 0 ? '-8px' : 'auto',
                top: position.top - 120 > 0 ? 'auto' : '-8px',
                borderBottom: position.top - 120 > 0 ? 'inherit' : 'none',
                borderRight: position.top - 120 > 0 ? 'inherit' : 'none',
                borderTop: position.top - 120 > 0 ? 'none' : 'inherit',
                borderLeft: position.top - 120 > 0 ? 'none' : 'inherit',
            }}
        ></div>
      </div>
    </div>
  );
};